import { useMemo, useCallback, useEffect } from 'react';
import { Checkbox, IconButton, Tooltip } from '@mui/material';

import { FCX } from '@models';
import { IconTrash } from '@svg';
import useTableSort from '@core/hooks/useTableSort';
import { SurveyDetailsItem } from '@core/api/useSurveys';
import { TableContext } from '@components/Table/TableContext';
import { SurveyStatusIdEnum } from '@core/hooks/useCommonEnums';
import { formatBoolean, formatDefaultDate, formatNumber } from '@core/utils/string';
import { iTableConfigColumnGroups, iTableConfigItem } from '@components/Table/TableConfig';
import { useSurveyItemSelection } from '@core/hooks/useSurveyItemSelection';

import './SurveyDetailsTable.scss';

type ItemRowValueProps = {
    isDate?: boolean;
    isBoolean?: boolean;
    defaultValue?: string;
} | undefined;

type IndexProps = {
    index: number;
};

const getItemRowRenderer = (surveyItems: SurveyDetailsItem[]) =>
    (field: keyof SurveyDetailsItem, {
        isDate,
        isBoolean,
        defaultValue
    }: ItemRowValueProps = {}) => {
        const ItemRowValue = ({ index }: IndexProps) => {
            const value = surveyItems?.[index]?.[field];
            if (isDate) return <>{formatDefaultDate(String(value)) ?? defaultValue ?? 'N/A'}</>;
            if (isBoolean) return <>{formatBoolean(Boolean(value))}</>;

            return <>{value ?? defaultValue}</>;
        };

        return ItemRowValue;
    };

const SurveyDetailsTableConfig: FCX<{
    data: SurveyDetailsItem[];
    surveyStatus?: number;
    onDeleteItems: (_ids?: number[]) => void;
    onSelectedItemsChange: (_selectedItems: number[]) => void;
}> = ({
    data,
    children,
    surveyStatus,
    onDeleteItems,
    onSelectedItemsChange
}) => {
    const {
        sortedResults,
        sortConfig
    } = useTableSort(data);

    const {
        selectedItems,
        selectAllVisible,
        deselectAllVisible,
        addItemToSelection,
        removeItemFromSelection,
        getHeaderCheckboxState
    } = useSurveyItemSelection({
        items: data,
        surveyStatus
    });

    useEffect(() => {
        onSelectedItemsChange(selectedItems);
    }, [selectedItems, onSelectedItemsChange]);

    const handleCheckboxSelect = useCallback((itemId: number) => {
        addItemToSelection(itemId);
    }, [addItemToSelection]);

    const handleCheckboxDeselect = useCallback((itemId: number) => {
        removeItemFromSelection(itemId);
    }, [removeItemFromSelection]);

    const handleHeaderCheckboxChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => (e.target.checked ? selectAllVisible() : deselectAllVisible()),
        [selectAllVisible, deselectAllVisible],
    );

    const handleRowCheckboxChange = useCallback(
        (itemId: number) => (e: React.ChangeEvent<HTMLInputElement>) => (e.target.checked ? handleCheckboxSelect(itemId) : handleCheckboxDeselect(itemId)),
        [handleCheckboxSelect, handleCheckboxDeselect],
    );

    const renderItemRowValue = useMemo(() => getItemRowRenderer(sortedResults), [sortedResults]);

    const renderSampleSizeValue = useCallback(({ index }: IndexProps) => (
        <>{formatNumber(sortedResults?.[index]?.currentSampleSize)} / {formatNumber(sortedResults?.[index]?.neededSampleSize)}</>
    ), [sortedResults]);

    const renderActionValue = useCallback((props: IndexProps) => {
        const index = props.index;
        const item = sortedResults?.[index];
        const itemId = item?.id;
        if (!itemId) return null;

        if (surveyStatus === SurveyStatusIdEnum.Draft) {
            return (
                <IconButton
                    onClick={() => onDeleteItems([itemId ?? 0])}
                    className="survey-details-table__delete-button"
                >
                    <IconTrash color="#FFA533" />
                </IconButton>
            );
        }

        const handleChange = handleRowCheckboxChange(itemId);
        const defaultChecked = surveyStatus === SurveyStatusIdEnum.Live ? item?.isClosed : item?.isPublished;

        return (
            <Checkbox
                checked={defaultChecked || selectedItems.includes(itemId)}
                disabled={defaultChecked}
                onChange={handleChange}
                color="success"
                size="small"
            />
        );
    }, [surveyStatus, sortedResults, selectedItems, onDeleteItems, handleRowCheckboxChange]);

    const allItemsIds = useMemo(() => data.map((item) => item.id ?? 0), [data]);

    const customColumns: iTableConfigItem[] = useMemo(() => {
        if (surveyStatus === SurveyStatusIdEnum.Draft) {
            return [
                {
                    key: 'priority',
                    sortingColName: 'priority',
                    title: 'Priority',
                    Value: renderItemRowValue('priority', { defaultValue: '—' }),
                    width: '70px',
                },
                {
                    key: 'action',
                    title: (
                        <Tooltip title={`Delete ${allItemsIds.length} items`}>
                            <IconButton
                                onClick={() => onDeleteItems(allItemsIds)}
                                className="survey-details-table__delete-button"
                            >
                                <IconTrash color="#FFA533" />
                            </IconButton>
                        </Tooltip>
                    ),
                    Value: renderActionValue,
                    width: '30px',
                },
            ];
        }

        if (surveyStatus === SurveyStatusIdEnum.Closed) {
            const publishHeaderState = getHeaderCheckboxState();
            return [
                {
                    key: 'sampleSize',
                    title: 'Sample Size',
                    Value: renderSampleSizeValue,
                    width: '90px',
                },
                {
                    key: 'priority',
                    sortingColName: 'priority',
                    title: 'Priority',
                    Value: renderItemRowValue('priority', { defaultValue: '—' }),
                    width: '70px',
                },
                {
                    key: 'publish',
                    title: (
                        <div className="survey-details-table__header-checkbox">
                            <Checkbox
                                checked={publishHeaderState.checked}
                                indeterminate={publishHeaderState.indeterminate}
                                onChange={handleHeaderCheckboxChange}
                                color="success"
                                size="small"
                            />
                            <span>Published</span>
                        </div>
                    ),
                    Value: renderActionValue,
                    width: '90px',
                },
            ];
        }

        const closeHeaderState = getHeaderCheckboxState();
        return [
            {
                key: 'sampleSize',
                title: 'Sample Size',
                Value: renderSampleSizeValue,
                width: '90px',
            },
            {
                key: 'priority',
                sortingColName: 'priority',
                title: 'Priority',
                Value: renderItemRowValue('priority', { defaultValue: '—' }),
                width: '70px',
            },
            {
                key: 'close',
                title: (
                    <div className="survey-details-table__header-checkbox">
                        <Checkbox
                            checked={closeHeaderState.checked}
                            indeterminate={closeHeaderState.indeterminate}
                            onChange={handleHeaderCheckboxChange}
                            color="success"
                            size="small"
                        />
                        <span>Closed</span>
                    </div>
                ),
                Value: renderActionValue,
                width: '90px',
            },
        ];
    }, [
        surveyStatus,
        getHeaderCheckboxState,
        handleHeaderCheckboxChange,
        renderActionValue,
        renderSampleSizeValue,
        renderItemRowValue,
        allItemsIds,
        onDeleteItems,
    ]);

    const tableConfig = useMemo(
        (): iTableConfigItem[] => [
            {
                key: 'id',
                sortingColName: 'id',
                title: 'ID',
                Value: renderItemRowValue('id'),
                width: '50px',
            },
            {
                key: 'date',
                sortingColName: 'date',
                title: 'Date',
                Value: renderItemRowValue('date', { isDate: true }),
                width: '70px',
            },
            {
                key: 'testType',
                sortingColName: 'testType',
                title: 'Test Type',
                width: '90px',
                Value: renderItemRowValue('testType'),
            },
            {
                key: 'companyTestedBy',
                sortingColName: 'companyName',
                title: 'Company (Tested By)',
                Value: renderItemRowValue('companyName'),
                width: '80px',
            },
            {
                key: 'chainSegmentTestedFor',
                sortingColName: 'testedFor',
                title: 'Chain/Segment (Tested For)',
                Value: renderItemRowValue('testedFor'),
                width: '90px',
            },
            {
                key: 'itemType',
                sortingColName: 'itemType',
                title: 'Item Type',
                Value: renderItemRowValue('itemType'),
                width: '90px',
            },
            {
                key: 'itemName',
                sortingColName: 'itemName',
                title: 'Item Name',
                Value: renderItemRowValue('itemName'),
                width: '160px',
            },
            {
                key: 'price',
                title: 'Price',
                Value: renderItemRowValue('price'),
                width: '70px',
            },
            {
                key: 'image',
                sortingColName: 'isPhoto',
                title: 'Image',
                Value: renderItemRowValue('isPhoto', { isBoolean: true }),
                width: '50px',
            },
            {
                key: 'exclusive',
                sortingColName: 'exclusive',
                title: 'Exclusive',
                Value: renderItemRowValue('exclusive', { isBoolean: true }),
                width: '50px',
            },
            ...customColumns,
        ],
        [customColumns, renderItemRowValue],
    );

    const configColumnGroups = useMemo((): iTableConfigColumnGroups[] => {
        if (surveyStatus === SurveyStatusIdEnum.Draft) {
            return [
                {
                    title: 'User Settings',
                    columnSpan: 10,
                    key: 'userSettings',
                },
                {
                    title: 'Admin Settings',
                    columnSpan: 1,
                    key: 'adminSettings',
                },
                {
                    title: '',
                    columnSpan: 1,
                    key: 'actions',
                },
            ];
        }

        return [
            {
                title: 'User Settings',
                columnSpan: 10,
                key: 'userSettings',
            },
            {
                title: 'Internal',
                columnSpan: surveyStatus === SurveyStatusIdEnum.Closed ? 3 : 4,
                key: 'internal',
            },
        ];
    }, [surveyStatus]);

    const contextValue = useMemo(() => ({
        config: tableConfig,
        configColumnGroups,
        sortConfig
    }), [tableConfig, configColumnGroups, sortConfig]);

    return (
        <TableContext.Provider value={contextValue}>
            {children}
        </TableContext.Provider>
    );
};

export default SurveyDetailsTableConfig;
